import React, { useState, useEffect } from 'react';
import { 
  Building, Users, Globe, Calendar, Save, Edit3, X, 
  AlertCircle, CheckCircle, Loader, Target, Heart, Lightbulb
} from 'lucide-react';

const TrueProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [organizationData, setOrganizationData] = useState(null);
  const [profileData, setProfileData] = useState({
    mission: '',
    vision: '',
    values: [],
    culture_description: '',
    team_structure: '',
    founding_year: null,
    key_achievements: [],
    completed: false,
    logo_url: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  // Fetch True Profile data on mount
  useEffect(() => {
    fetchTrueProfile();
  }, []);

  const fetchTrueProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const orgId = localStorage.getItem('cness_organization_id') || sessionStorage.getItem('cness_organization_id');
      
      console.log('🔍 TrueProfile checking storage:', orgId);
      
      if (!orgId) {
        console.error('❌ No organization ID in sessionStorage');
        console.log('All sessionStorage keys:', Object.keys(sessionStorage));
        setError('No organization ID found. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${backendUrl}/api/true-profile/${orgId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch true profile');
      }

      const data = await response.json();
      setOrganizationData(data.organization);
      
      // Set profile data (either existing or empty)
      setProfileData({
        mission: data.profile.mission || '',
        vision: data.profile.vision || '',
        values: data.profile.values || [],
        culture_description: data.profile.culture_description || '',
        team_structure: data.profile.team_structure || '',
        founding_year: data.profile.founding_year || null,
        key_achievements: data.profile.key_achievements || [],
        completed: data.profile.completed || false
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching true profile:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');

      const orgId = sessionStorage.getItem('cness_organization_id');
      
      // Check if required fields are filled to mark as completed
      const isCompleted = 
        profileData.mission.trim() !== '' &&
        profileData.vision.trim() !== '' &&
        profileData.values.length > 0;

      const saveData = {
        organization_id: orgId,
        ...profileData,
        completed: isCompleted
      };

      const response = await fetch(`${backendUrl}/api/true-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      if (!response.ok) {
        throw new Error('Failed to save true profile');
      }

      const result = await response.json();
      
      setProfileData(prev => ({ ...prev, completed: isCompleted }));
      setSuccessMessage('True Profile saved successfully!');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);

      setSaving(false);
    } catch (err) {
      console.error('Error saving true profile:', err);
      setError(err.message);
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...profileData[field]];
    newArray[index] = value;
    setProfileData(prev => ({
      ...prev,
      [field]: newArray
    }));
  };

  const addArrayItem = (field) => {
    setProfileData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setProfileData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="h-8 w-8 text-blue-600 animate-spin mx-auto" />
          <p className="text-gray-600 mt-4">Loading True Profile...</p>
        </div>
      </div>
    );
  }

  if (error && !organizationData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-red-800 font-semibold">Error Loading Profile</h3>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchTrueProfile}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organization True Profile</h1>
            <p className="text-gray-600 mt-2">
              Complete your organizational profile to unlock the assessment process
            </p>
            <div className="flex items-center space-x-4 mt-3">
              {profileData.completed ? (
                <div className="flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <CheckCircle className="h-4 w-4" />
                  <span>Profile Completed</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                  <AlertCircle className="h-4 w-4" />
                  <span>Profile Incomplete</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex space-x-3">
            {!isEditing ? (
              <button 
                onClick={() => setIsEditing(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Edit3 className="h-4 w-4" />
                <span>Edit Profile</span>
              </button>
            ) : (
              <div className="flex space-x-2">
                <button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                </button>
                <button 
                  onClick={() => {
                    setIsEditing(false);
                    fetchTrueProfile(); // Reset to saved data
                  }}
                  disabled={saving}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Form */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Organization Logo Upload */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-4">
              <Building className="h-6 w-6 text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-900">Organization Logo</h2>
            </div>
            
            <div className="space-y-4">
              {organizationData?.logo_url && (
                <div className="flex items-center space-x-4">
                  <img 
                    src={organizationData.logo_url} 
                    alt="Organization Logo" 
                    className="h-20 w-20 object-contain rounded-lg border-2 border-gray-200"
                  />
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Current Logo</p>
                    <p className="text-xs text-gray-500 mt-1">Displayed on all certificates and badges</p>
                  </div>
                </div>
              )}
              
              {isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Upload New Logo
                  </label>
                  
                  <div className="flex items-center space-x-4">
                    <label className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-center px-6 py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all">
                        <div className="text-center">
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p className="mt-2 text-sm font-medium text-gray-700">
                            Click to browse
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            PNG, JPG, SVG up to 5MB
                          </p>
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Convert to base64 for preview and storage
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              handleInputChange('logo_url', reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                  
                  {profileData.logo_url && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-green-800">Logo selected</p>
                          <p className="text-xs text-green-600">Preview will appear after saving</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-3">
                    📋 Recommended: Square format (1:1 ratio), minimum 200x200px, PNG or SVG for best quality
                  </p>
                </div>
              )}
              
              {!organizationData?.logo_url && !isEditing && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Building className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No logo uploaded</p>
                  <p className="text-sm text-gray-500 mt-1">Click Edit to upload your organization logo</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Mission Statement */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-4">
              <Target className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Mission Statement</h2>
            </div>
            
            {isEditing ? (
              <textarea
                value={profileData.mission}
                onChange={(e) => handleInputChange('mission', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe your organization's mission and purpose..."
              />
            ) : (
              <p className="text-gray-700 leading-relaxed">
                {profileData.mission || <span className="text-gray-400 italic">Mission statement not yet added. Click "Edit Profile" to add it.</span>}
              </p>
            )}
          </div>

          {/* Vision Statement */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-4">
              <Lightbulb className="h-6 w-6 text-yellow-600" />
              <h2 className="text-xl font-semibold text-gray-900">Vision Statement</h2>
            </div>
            
            {isEditing ? (
              <textarea
                value={profileData.vision}
                onChange={(e) => handleInputChange('vision', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe your organization's vision for the future..."
              />
            ) : (
              <p className="text-gray-700 leading-relaxed">
                {profileData.vision || <span className="text-gray-400 italic">Vision statement not yet added. Click "Edit Profile" to add it.</span>}
              </p>
            )}
          </div>

          {/* Core Values */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-4">
              <Heart className="h-6 w-6 text-red-600" />
              <h2 className="text-xl font-semibold text-gray-900">Core Values</h2>
            </div>
            
            {isEditing ? (
              <div className="space-y-3">
                {profileData.values.map((value, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleArrayChange('values', index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Core value ${index + 1}`}
                    />
                    <button
                      onClick={() => removeArrayItem('values', index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem('values')}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center space-x-1"
                >
                  <span>+ Add Core Value</span>
                </button>
              </div>
            ) : (
              <div>
                {profileData.values.length > 0 ? (
                  <ul className="space-y-2">
                    {profileData.values.map((value, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span className="text-gray-700">{value}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-400 italic">No core values added yet. Click "Edit Profile" to add them.</p>
                )}
              </div>
            )}
          </div>

          {/* Culture Description */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="h-6 w-6 text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-900">Culture & Work Environment</h2>
            </div>
            
            {isEditing ? (
              <textarea
                value={profileData.culture_description}
                onChange={(e) => handleInputChange('culture_description', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe your organizational culture and work environment..."
              />
            ) : (
              <p className="text-gray-700 leading-relaxed">
                {profileData.culture_description || <span className="text-gray-400 italic">Culture description not yet added. Click "Edit Profile" to add it.</span>}
              </p>
            )}
          </div>

          {/* Team Structure */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-4">
              <Building className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Team Structure</h2>
            </div>
            
            {isEditing ? (
              <textarea
                value={profileData.team_structure}
                onChange={(e) => handleInputChange('team_structure', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe your team structure and organization..."
              />
            ) : (
              <p className="text-gray-700 leading-relaxed">
                {profileData.team_structure || <span className="text-gray-400 italic">Team structure not yet added. Click "Edit Profile" to add it.</span>}
              </p>
            )}
          </div>

          {/* Key Achievements */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Key Achievements</h2>
            </div>
            
            {isEditing ? (
              <div className="space-y-3">
                {profileData.key_achievements.map((achievement, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={achievement}
                      onChange={(e) => handleArrayChange('key_achievements', index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Achievement ${index + 1}`}
                    />
                    <button
                      onClick={() => removeArrayItem('key_achievements', index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem('key_achievements')}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center space-x-1"
                >
                  <span>+ Add Achievement</span>
                </button>
              </div>
            ) : (
              <div>
                {profileData.key_achievements.length > 0 ? (
                  <ul className="space-y-2">
                    {profileData.key_achievements.map((achievement, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-green-600 mt-1">✓</span>
                        <span className="text-gray-700">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-400 italic">No achievements added yet. Click "Edit Profile" to add them.</p>
                )}
              </div>
            )}
          </div>

        </div>

        {/* Right Column - Organization Info */}
        <div className="space-y-6">
          
          {/* Basic Organization Info */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Organization Name</label>
                <p className="text-gray-900 mt-1 font-semibold">{organizationData?.name || organizationData?.organization_name || 'N/A'}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">Industry</label>
                <p className="text-gray-900 mt-1">{organizationData?.industry || 'N/A'}</p>
              </div>

              {(organizationData?.organization_type || organizationData?.size) && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Organization Type & Size</label>
                  <p className="text-gray-900 mt-1">{organizationData?.organization_type || organizationData?.size || 'N/A'}</p>
                </div>
              )}

              {organizationData?.size && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Size</label>
                  <p className="text-gray-900 mt-1">{organizationData.size}</p>
                </div>
              )}

              {(organizationData?.location || organizationData?.headquarters) && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Location</label>
                  <p className="text-gray-900 mt-1">{organizationData?.location || organizationData?.headquarters || 'N/A'}</p>
                </div>
              )}

              {(organizationData?.employees || organizationData?.employee_count) && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Employees</label>
                  <p className="text-gray-900 mt-1">{organizationData?.employees || organizationData?.employee_count || 'N/A'}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <p className="text-gray-900 mt-1">{organizationData?.email || 'N/A'}</p>
              </div>

              {organizationData?.website && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Website</label>
                  <a 
                    href={organizationData.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline mt-1 flex items-center space-x-1"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="text-sm">{organizationData.website}</span>
                  </a>
                </div>
              )}

              {organizationData?.status && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <p className="text-gray-900 mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      organizationData.status === 'approved' ? 'bg-green-100 text-green-800' :
                      organizationData.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {organizationData.status.charAt(0).toUpperCase() + organizationData.status.slice(1)}
                    </span>
                  </p>
                </div>
              )}

              {(organizationData?.registered_at || organizationData?.created_at) && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Registered</label>
                  <p className="text-gray-900 mt-1">
                    {new Date(organizationData?.registered_at || organizationData?.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              )}

              {(organizationData?.about || organizationData?.description) && (
                <div>
                  <label className="text-sm font-medium text-gray-600">About</label>
                  <p className="text-gray-700 mt-1 text-sm leading-relaxed">{organizationData?.about || organizationData?.description || 'N/A'}</p>
                </div>
              )}

              {isEditing && (
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Founding Year</label>
                  <input
                    type="number"
                    value={profileData.founding_year || ''}
                    onChange={(e) => handleInputChange('founding_year', parseInt(e.target.value) || null)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 2020"
                    min="1800"
                    max={new Date().getFullYear()}
                  />
                </div>
              )}

              {!isEditing && profileData.founding_year && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Founding Year</label>
                  <p className="text-gray-900 mt-1">{profileData.founding_year}</p>
                </div>
              )}
            </div>
          </div>

          {/* Completion Status */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Profile Completion</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Mission Statement</span>
                {profileData.mission ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Vision Statement</span>
                {profileData.vision ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Core Values</span>
                {profileData.values.length > 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
            
            {!profileData.completed && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>Required:</strong> Complete Mission, Vision, and at least one Core Value to unlock assessments.
                </p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default TrueProfile;
